import {
    centsToFormattedDollars,
    convertDecimalToPercentageForDisplay,
    getOrdinalSuffixedNumber,
    isEmpty,
    transformEnumToDisplay,
} from '@fruitful/fruitils';
import {
    type DeepPartial,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountDetails,
    MoneyMapCategory,
    type MoneyMapRule,
    MoneyMapRuleContributionType,
    MoneyMapRuleGoalType,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    Portfolio,
    ReadableApexAccountRegistrationType,
} from '@fruitful/types';
import type { ReactNode } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Icon,
    LabelRegular,
    redactPrivate,
    SuperscriptRegular,
    TargetIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import DashCurve from '../../assets/dash-curve.svg';
import {
    deriveRuleContributionType,
    getSyntheticGoalPriorityFromActual,
} from '../utils/moneyMapRuleUtils';
import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';

export type RuleVariant = 'full' | 'summary';

interface MoneyMapRulePriorityProps {
    priority: number;
}

export const MoneyMapRulePriority = ({
    priority,
}: MoneyMapRulePriorityProps) => {
    return (
        <XStack
            width={32}
            height={32}
            alignItems="center"
            justifyContent="center"
            backgroundColor="$orchidPink1200"
            borderRadius={16}
        >
            <LabelRegular color="$orchidPink400">
                {getOrdinalSuffixedNumber(priority)}
            </LabelRegular>
        </XStack>
    );
};

interface MoneyMapRulePercentageProps {
    percentage: number;
}

export const MoneyMapRulePercentage = ({
    percentage,
}: MoneyMapRulePercentageProps) => {
    return (
        <XStack
            width={32}
            height={32}
            alignItems="center"
            justifyContent="center"
            backgroundColor="$white"
            borderRadius={50}
            borderWidth={1}
            borderColor="$orchidPink600"
        >
            <SuperscriptRegular color="$peach1200">
                {convertDecimalToPercentageForDisplay(percentage)}
            </SuperscriptRegular>
        </XStack>
    );
};

interface MoneyMapRuleGoalHeaderProps {
    rule: MoneyMapRule;
    groupPriority?: number;
    /** Loan/target row invalid (matches publish validation). */
    invalidTargetBalance?: boolean;
}

const MoneyMapRuleGoalHeader = ({
    rule,
    groupPriority,
    invalidTargetBalance = false,
}: MoneyMapRuleGoalHeaderProps) => {
    const { priority, description, targetBalanceCents, moneyMapRuleGroupId } =
        rule;

    const targetColor = invalidTargetBalance ? '$red900' : '$gray900';
    const showTargetAmount = targetBalanceCents != null || invalidTargetBalance;

    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            {...redactPrivate()}
        >
            <XStack gap={8} alignItems="center" flex={1}>
                {moneyMapRuleGroupId != null && groupPriority != null ? (
                    <MoneyMapRulePriority priority={groupPriority} />
                ) : (
                    <MoneyMapRulePriority priority={priority} />
                )}
                <BodyMediumRegular flex={1}>{description}</BodyMediumRegular>
            </XStack>
            {showTargetAmount && (
                <XStack gap={8} alignItems="center">
                    <Icon color="$gray900" size={14} icon={TargetIcon} />
                    <BodyMediumRegular color={targetColor}>
                        {centsToFormattedDollars(
                            targetBalanceCents ?? 0,
                            false,
                        )}
                    </BodyMediumRegular>
                </XStack>
            )}
        </XStack>
    );
};

interface RuleParameterLabelProps {
    description: string;
    value?: string;
    /** Highlight loan/target line when missing or non-positive (publish blocked). */
    isError?: boolean;
}

export const RuleParameterLabel = ({
    description,
    value,
    isError = false,
}: RuleParameterLabelProps) => {
    const textColor = isError ? '$red900' : '$gray900';
    return (
        <XStack alignItems="center" gap={4}>
            <LabelRegular color={textColor}>{description}</LabelRegular>
            {value != null && (
                <LabelRegular
                    color={textColor}
                    backgroundColor="$white"
                    paddingVertical={2}
                    paddingHorizontal={4}
                    borderRadius={4}
                    {...redactPrivate()}
                >
                    {value}
                </LabelRegular>
            )}
        </XStack>
    );
};

interface MoneyMapRuleGoalDetailsProps {
    rule: MoneyMapRule;
    invalidTargetBalance?: boolean;
}

const MoneyMapRuleGoalDetails = ({
    rule,
    invalidTargetBalance = false,
}: MoneyMapRuleGoalDetailsProps) => {
    const {
        goalType,
        targetBalanceCents,
        percentage,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
        moneyMapRuleGroupId,
    } = rule;
    const contributionType = deriveRuleContributionType(rule);
    const ruleBelongsToGroup = moneyMapRuleGroupId != null;

    const targetValueForDisplay =
        targetBalanceCents != null || invalidTargetBalance
            ? centsToFormattedDollars(targetBalanceCents ?? 0, false)
            : undefined;

    return (
        <YStack
            marginLeft={16}
            gap={4}
            paddingLeft={8}
            borderLeftWidth={1}
            borderLeftColor="$gray900"
        >
            {goalType !== MoneyMapRuleGoalType.CONTRIBUTE_INDEFINITELY && (
                <RuleParameterLabel
                    description={transformEnumToDisplay(goalType ?? '')}
                    value={targetValueForDisplay}
                    isError={invalidTargetBalance}
                />
            )}
            {contributionType != null && (
                <RuleParameterLabel
                    description={
                        ruleBelongsToGroup
                            ? 'Percentage'
                            : transformEnumToDisplay(contributionType)
                    }
                    value={
                        percentage != null &&
                        contributionType !==
                            MoneyMapRuleContributionType.ALL_REMAINING_FUNDS
                            ? convertDecimalToPercentageForDisplay(percentage)
                            : ruleBelongsToGroup
                              ? convertDecimalToPercentageForDisplay(1)
                              : undefined
                    }
                />
            )}
            {monthlyContributionMaxCents != null && (
                <RuleParameterLabel
                    description="Monthly limit"
                    value={centsToFormattedDollars(
                        monthlyContributionMaxCents,
                        false,
                    )}
                />
            )}
            {yearlyContributionMaxCents != null && (
                <RuleParameterLabel
                    description="Annual limit"
                    value={centsToFormattedDollars(
                        yearlyContributionMaxCents,
                        false,
                    )}
                />
            )}
        </YStack>
    );
};

const getAccountTypeLabel = (
    account?: DeepPartial<MoneyMapAccountWithAccountDetails>,
): string => {
    const { status, type, accountDetails } = account ?? {};
    const { institutionName, accountNumberMasked } = accountDetails ?? {};

    if (type === MoneyMapAccountType.INTERNAL_BANKING) {
        const accountNumber =
            accountNumberMasked != null && accountNumberMasked.length > 0
                ? ` (*${accountNumberMasked.slice(-4)})`
                : '';

        return `${status === MoneyMapAccountStatus.PROPOSED ? 'New ' : ''}Fruitful Cash Account${accountNumber}`;
    }

    if (type === MoneyMapAccountType.INVESTMENT_MANAGEMENT) {
        return `${status === MoneyMapAccountStatus.PROPOSED ? 'New ' : ''}Fruitful Investment Account`;
    }

    if (type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        const { apexAccountType, accountNumberMasked: maskedNumber } =
            (accountDetails ?? {}) as MoneyMapApexAccountDetails;
        const accountNumber =
            maskedNumber != null && maskedNumber.length > 0
                ? ` (*${maskedNumber.slice(-4)})`
                : '';
        const accountTypeLabel =
            ReadableApexAccountRegistrationType[apexAccountType ?? ''] ??
            'Investment Account';
        return `${status === MoneyMapAccountStatus.PROPOSED ? 'New ' : ''}Fruitful ${accountTypeLabel} ${accountNumber}`;
    }

    if (!isEmpty(institutionName)) {
        return `${institutionName} *${accountNumberMasked ?? 'XXXX'}`;
    }

    return 'Unknown';
};

const getImAccountPortfolioLabel = (
    account?: DeepPartial<MoneyMapAccountWithAccountDetails>,
) => {
    if (account == null) {
        return '';
    }
    const { type } = account;
    if (type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        const { apexPortfolio, apexRiskLevel } = (account.accountDetails ??
            {}) as MoneyMapApexAccountDetails;
        return apexPortfolio != null && apexRiskLevel != null
            ? `(${apexPortfolio === Portfolio.CORE ? 'Core' : 'ESG'} ${apexRiskLevel})`
            : '';
    }

    return '';
};

/** Account kind, display name, and Apex model (e.g. `(Core 10)`) — shared by goal account rows. */
const MoneyMapAccountTypeNameAndModel = ({
    account,
    nameRowTrailing,
}: {
    account?: DeepPartial<MoneyMapAccountWithAccountDetails>;
    nameRowTrailing?: ReactNode;
}) => {
    const portfolioLabel = getImAccountPortfolioLabel(account);
    return (
        <YStack flex={1}>
            <LabelRegular color="$gray900">
                {getAccountTypeLabel(account)}
            </LabelRegular>
            <XStack alignItems="center" gap={8}>
                <BodyMediumRegular>
                    {account?.accountDetails?.name}
                    {portfolioLabel !== '' && (
                        <LabelRegular color="$gray900">
                            {`  ${portfolioLabel}`}
                        </LabelRegular>
                    )}
                </BodyMediumRegular>
                {nameRowTrailing}
            </XStack>
        </YStack>
    );
};

interface MoneyMapAccountProps {
    category: MoneyMapCategory;
    rule: DeepPartial<MoneyMapRuleWithAccountDetails>;
    moneyMapAccountForTransfer?: DeepPartial<MoneyMapAccountWithAccountDetails>;
    /** Roth side of a backdoor Roth pair (Trad is on the rule's `moneyMapAccount`). */
    backdoorRothPairedAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>;
    showIncomeAccountBadge?: boolean;
}

const MoneyMapAccount = ({
    category,
    rule,
    moneyMapAccountForTransfer,
    backdoorRothPairedAccount,
    showIncomeAccountBadge,
}: MoneyMapAccountProps) => {
    const { moneyMapAccount, type } = rule;
    const showBackdoorRothPairing =
        backdoorRothPairedAccount != null &&
        moneyMapAccount != null &&
        moneyMapAccountForTransfer == null;
    const showExternalDestinationAccount =
        !showBackdoorRothPairing && moneyMapAccountForTransfer != null;
    const isGoalAccount = category === MoneyMapCategory.GOALS;

    const accountForTransfer = moneyMapAccountForTransfer ?? moneyMapAccount;
    const externalDestinationAccount = showExternalDestinationAccount
        ? moneyMapAccount
        : undefined;

    return (
        <YStack
            borderRadius={12}
            backgroundColor="$white"
            padding={isGoalAccount ? 12 : 0}
        >
            <XStack alignItems="center" justifyContent="space-between">
                <XStack flex={1} gap={12} alignItems="center">
                    <MoneyMapAccountIcon
                        category={category}
                        account={accountForTransfer ?? {}}
                    />
                    <MoneyMapAccountTypeNameAndModel
                        account={accountForTransfer}
                        nameRowTrailing={
                            showIncomeAccountBadge ? (
                                <View
                                    paddingHorizontal={6}
                                    paddingVertical={2}
                                    borderRadius={12}
                                    backgroundColor="$green500"
                                >
                                    <LabelRegular>Income account</LabelRegular>
                                </View>
                            ) : undefined
                        }
                    />
                </XStack>
                {(type === MoneyMapRuleType.BILLS ||
                    type === MoneyMapRuleType.SPEND) && (
                    <BodyMediumEmphasized {...redactPrivate()}>
                        {centsToFormattedDollars(
                            rule.targetBalanceCents ??
                                rule.monthlyContributionMaxCents ??
                                0,
                            false,
                        )}
                    </BodyMediumEmphasized>
                )}
            </XStack>
            {showBackdoorRothPairing && (
                <XStack>
                    <View marginLeft={14}>
                        <DashCurve />
                    </View>
                    <XStack
                        flex={1}
                        gap={12}
                        alignItems="center"
                        paddingTop={10}
                    >
                        <MoneyMapAccountIcon
                            category={category}
                            account={backdoorRothPairedAccount ?? {}}
                        />
                        <MoneyMapAccountTypeNameAndModel
                            account={backdoorRothPairedAccount}
                        />
                    </XStack>
                </XStack>
            )}
            {showExternalDestinationAccount && (
                <XStack>
                    <View marginLeft={14}>
                        <DashCurve />
                    </View>
                    <XStack
                        flex={1}
                        gap={12}
                        alignItems="center"
                        paddingTop={10}
                    >
                        <MoneyMapAccountIcon
                            category={category}
                            account={externalDestinationAccount ?? {}}
                        />
                        <MoneyMapAccountTypeNameAndModel
                            account={externalDestinationAccount}
                        />
                    </XStack>
                </XStack>
            )}
        </YStack>
    );
};

interface MoneyMapAccountRuleProps {
    category: MoneyMapCategory;
    rule: DeepPartial<MoneyMapRuleWithAccountDetails>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>;
    onPress?: () => void;
    showIncomeAccountBadge?: boolean;
    variant?: RuleVariant;
    /** e.g. debt pay down / build target with no positive loan or target balance (blocks publish). */
    showInvalidGoalTargetBalance?: boolean;
}

export const MoneyMapAccountRule = ({
    category,
    rule,
    moneyMap,
    accountsById,
    onPress,
    showIncomeAccountBadge,
    variant = 'full',
    showInvalidGoalTargetBalance = false,
}: MoneyMapAccountRuleProps) => {
    const isGoal = rule.type === MoneyMapRuleType.GOAL;
    const hoverStyle = onPress != null ? { cursor: 'pointer' } : {};

    const syntheticPriority =
        isGoal && rule.priority != null
            ? getSyntheticGoalPriorityFromActual(rule.priority, moneyMap)
            : rule.priority;

    rule = {
        ...rule,
        priority: syntheticPriority,
    };

    const backdoorRothPairedAccount =
        rule.moneyMapAccount?.backdoorRothLinkedMoneyMapAccount ??
        (rule.moneyMapAccount?.backdoorRothLinkedMoneyMapAccountId != null
            ? accountsById[
                  rule.moneyMapAccount.backdoorRothLinkedMoneyMapAccountId
              ]
            : undefined);

    const moneyMapAccountForTransfer =
        backdoorRothPairedAccount != null
            ? undefined
            : rule.moneyMapAccount?.moneyMapAccountIdForTransfer != null
              ? accountsById[rule.moneyMapAccount?.moneyMapAccountIdForTransfer]
              : undefined;

    if (!isGoal) {
        return (
            <View onPress={onPress} hoverStyle={hoverStyle}>
                <MoneyMapAccount
                    category={category}
                    rule={rule}
                    moneyMapAccountForTransfer={moneyMapAccountForTransfer}
                    backdoorRothPairedAccount={backdoorRothPairedAccount}
                    showIncomeAccountBadge={showIncomeAccountBadge}
                />
            </View>
        );
    }

    if (variant === 'summary') {
        const groupPriority =
            rule.moneyMapRuleGroupId != null
                ? moneyMap.moneyMapRuleGroups?.find(
                      (group) => group.id === rule.moneyMapRuleGroupId,
                  )?.priority
                : undefined;

        return (
            <YStack gap={12} onPress={onPress} hoverStyle={hoverStyle}>
                <MoneyMapRuleGoalHeader
                    rule={rule as MoneyMapRule}
                    groupPriority={
                        groupPriority != null
                            ? getSyntheticGoalPriorityFromActual(
                                  groupPriority,
                                  moneyMap,
                              )
                            : undefined
                    }
                    invalidTargetBalance={showInvalidGoalTargetBalance}
                />
            </YStack>
        );
    }

    const groupPriority =
        rule.moneyMapRuleGroupId != null
            ? moneyMap.moneyMapRuleGroups?.find(
                  (group) => group.id === rule.moneyMapRuleGroupId,
              )?.priority
            : undefined;

    return (
        <YStack
            gap={12}
            padding={12}
            borderRadius={12}
            backgroundColor="$gray200"
            borderWidth={showInvalidGoalTargetBalance ? 2 : 0}
            borderColor={showInvalidGoalTargetBalance ? '$red900' : undefined}
            onPress={onPress}
            hoverStyle={hoverStyle}
        >
            <MoneyMapRuleGoalHeader
                rule={rule as MoneyMapRule}
                groupPriority={
                    groupPriority != null
                        ? getSyntheticGoalPriorityFromActual(
                              groupPriority,
                              moneyMap,
                          )
                        : undefined
                }
                invalidTargetBalance={showInvalidGoalTargetBalance}
            />
            <MoneyMapRuleGoalDetails
                rule={rule as MoneyMapRule}
                invalidTargetBalance={showInvalidGoalTargetBalance}
            />
            <MoneyMapAccount
                category={category}
                rule={rule}
                moneyMapAccountForTransfer={moneyMapAccountForTransfer}
                backdoorRothPairedAccount={backdoorRothPairedAccount}
                showIncomeAccountBadge={showIncomeAccountBadge}
            />
        </YStack>
    );
};

interface MoneyMapIncomeAccountProps {
    account: DeepPartial<MoneyMapAccountWithAccountDetails>;
    onPress?: () => void;
}

export const MoneyMapIncomeAccount = ({
    account,
    onPress,
}: MoneyMapIncomeAccountProps) => {
    return (
        <XStack
            gap={12}
            alignItems="center"
            onPress={onPress}
            hoverStyle={onPress != null ? { cursor: 'pointer' } : {}}
        >
            <MoneyMapAccountIcon
                category={MoneyMapCategory.INCOME}
                account={account}
            />
            <YStack>
                <LabelRegular color="$gray900">
                    {getAccountTypeLabel(account)}
                </LabelRegular>
                <BodyMediumRegular>
                    {account.accountDetails?.name}
                </BodyMediumRegular>
                <LabelRegular color="$gray900">
                    {getImAccountPortfolioLabel(account)}
                </LabelRegular>
            </YStack>
        </XStack>
    );
};
